import { useEffect, useState, useMemo, useCallback } from 'react';
import { ProgramRequest } from '@/types/models';
import { ProgramRequestFilters } from '@/types/programRequests';
import logger from '@/layers/support/monitoring/logger';
import { fetchProgramRequests } from '@/layers/presentation/services/programRequests';

const LOG_MODULE = 'FE [Presentation / Hooks / ProgramRequests]';

export function useProgramRequestsList(
  filters?: ProgramRequestFilters,
  isFiltersLoading?: boolean,
) {
  const [programRequests, setProgramRequests] = useState<ProgramRequest[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Memoize filters to prevent unnecessary re-renders
  const memoizedFilters = useMemo(() => filters, [filters]);

  const fetchProgramRequestsData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const fetchedProgramRequests = await fetchProgramRequests(memoizedFilters);
      setProgramRequests(fetchedProgramRequests);
      logger.debug(`${LOG_MODULE} useProgramRequestsList`, {
        memoizedFilters,
        fetchProgramRequests,
      });
    } catch (error) {
      logger.error(`${LOG_MODULE} useProgramRequestsList: error`, { error });
      setError((error as Error).message);
      setProgramRequests([]);
    } finally {
      setIsLoading(false);
    }
  }, [memoizedFilters]);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        await fetchProgramRequestsData();
      } catch (error) {
        if (isMounted) {
          // Error handling is already done in fetchProgramRequestsData
          // This catch block is just to prevent unhandled promise rejections
        }
      }
    };

    if (!isFiltersLoading) {
      fetchData();
    }

    return () => {
      isMounted = false;
    };
  }, [fetchProgramRequestsData, isFiltersLoading]);

  return { programRequests, isLoading, error, refetch: fetchProgramRequestsData };
}
